import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { api } from "@/api";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { AudienceBadge, blankPromoDraft, discountLabel, num, PARTNER_KIND_LABEL, PartnerEditorDialog, partnerInputFrom, PeriodFilter, type Period, PromoEditorDialog, rewardLabel, rub, StateBadge, StatTile, dateRu } from "@/components/promo-kit";
import { Button } from "@osmix/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCanManagePromos } from "./promos";

/**
 * Карточка партнёра: контакты, все его промокоды и сводная статистика —
 * привлечённые продавцы и покупатели, продажи, начисленное вознаграждение.
 * Выплат нет: «начислено» — расчёт по условиям кодов.
 */
export function PartnerDetailPage() {
  const {
    id = ""
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const period: Period = {
    from: params.get("from") ?? "",
    to: params.get("to") ?? ""
  };
  const setPeriod = (p: Period) => {
    const next = new URLSearchParams(params);
    if (p.from) next.set("from", p.from);else next.delete("from");
    if (p.to) next.set("to", p.to);else next.delete("to");
    setParams(next, {
      replace: true
    });
  };
  const q = useQuery({
    queryKey: ["partners", "detail", id, period.from, period.to],
    queryFn: () => api.partners.detail(id, period)
  });
  const canManage = useCanManagePromos();
  const [editing, setEditing] = useState(false);
  const [newPromo, setNewPromo] = useState<null | "buyer" | "seller">(null);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({
    queryKey: ["partners"]
  });
  const back = <Link to="/promos?tab=partners" className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
			<ArrowLeft className="size-4" />
			Партнёры
		</Link>;
  if (q.error) return <div className="flex flex-col gap-4">
				{back}
				<p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{(q.error as Error).message}</p>
			</div>;
  if (!q.data) return <p className="text-sm text-muted-foreground">Загружаем…</p>;
  const {
    partner,
    promos,
    stats
  } = q.data;
  const t = stats.totals;
  const byCode = new Map(stats.byPromo.map(b => [b.code, b.totals]));
  return <div className="flex flex-col gap-6" data-testid="partner-detail">
			{back}
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<div className="flex items-center gap-2">
						<h1 className="text-2xl font-semibold">{partner.name}</h1>
						<span className="rounded-full bg-muted px-2 py-0.5 text-xs">{PARTNER_KIND_LABEL[partner.kind]}</span>
						{!partner.active && <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">Выключен</span>}
					</div>
					<div className="mt-1 text-sm text-muted-foreground">
						{[partner.contactName, partner.phone, partner.email, partner.telegram].filter(Boolean).join(" · ") || "Контакты не указаны"}
					</div>
					{partner.user && <div className="mt-1 text-sm">
							Пользователь площадки:{" "}
							<Link className="hover:underline" to={`/users/${partner.user.id}`}>
								{partner.user.shopName || partner.user.name || partner.user.email}
							</Link>
						</div>}
					{partner.note && <p className="mt-2 max-w-2xl whitespace-pre-line text-sm">{partner.note}</p>}
				</div>
				<div className={canManage ? "flex flex-wrap gap-2" : "hidden"}>
					<Button variant="outline" size="sm" onClick={() => setNewPromo("buyer")} data-testid="partner-new-buyer-promo">
						<Plus className="mr-1.5 size-4" />
						Покупательский код
					</Button>
					<Button variant="outline" size="sm" onClick={() => setNewPromo("seller")} data-testid="partner-new-seller-promo">
						<Plus className="mr-1.5 size-4" />
						Продавцовый код
					</Button>
					<Button size="sm" onClick={() => setEditing(true)}>
						<Pencil className="mr-1.5 size-4" />
						Изменить
					</Button>
					<Button variant="ghost" size="sm" onClick={() => setRemoving(true)} title="Удалить партнёра">
						<Trash2 className="size-4 text-destructive" />
					</Button>
				</div>
			</div>
			{error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

			<PeriodFilter value={period} onChange={setPeriod} resolved={{
      from: stats.from,
      to: stats.to
    }} />

			<div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="partner-kpis">
				<StatTile label="Привлечено продавцов" value={num(t.sellersRegistered)} hint={`модерацию прошли: ${num(t.sellersApproved)}`} />
				<StatTile label="Продажи привлечённых продавцов" value={rub(t.sellerGmv)} hint={`комиссия площадки: ${rub(t.sellerCommission)}`} />
				<StatTile label="Привлечено покупателей" value={num(t.attractedBuyers)} hint={`их покупки: ${rub(t.attractedGmv)}`} />
				<StatTile label="Заказы по кодам" value={num(t.orders)} hint={`покупателей: ${num(t.buyers)}`} />
				<StatTile label="Выручка после скидки" value={rub(t.revenue)} hint={`скидки: ${rub(t.discount)}`} />
				<StatTile label="Начислено партнёру" value={rub(t.reward)} hint="расчётно, выплаты — позже" />
			</div>

			<section className="flex flex-col gap-2" data-testid="partner-promos">
				<h2 className="text-sm font-medium">
					Промокоды партнёра <span className="text-muted-foreground">{num(promos.length)}</span>
				</h2>
				<div className="overflow-x-auto rounded-lg border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Код</TableHead>
								<TableHead>Тип</TableHead>
								<TableHead>Скидка</TableHead>
								<TableHead>Статус</TableHead>
								<TableHead className="text-right">За период</TableHead>
								<TableHead className="text-right">Оборот</TableHead>
								<TableHead>Вознаграждение</TableHead>
								<TableHead className="text-right">Начислено</TableHead>
								<TableHead>Создан</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{promos.length === 0 && <TableRow>
									<TableCell colSpan={9} className="py-6 text-center text-sm text-muted-foreground">
										У партнёра пока нет промокодов
									</TableCell>
								</TableRow>}
							{promos.map(p_0 => {
              const pt = byCode.get(p_0.code);
              const seller = p_0.audience === "seller";
              return <TableRow key={p_0.code} className="cursor-pointer" onClick={() => navigate(`/promos/${encodeURIComponent(p_0.code)}`)}>
										<TableCell className="font-mono font-medium">{p_0.code}</TableCell>
										<TableCell>
											<AudienceBadge audience={p_0.audience} />
										</TableCell>
										<TableCell className="text-sm">{discountLabel(p_0)}</TableCell>
										<TableCell>
											<StateBadge state={p_0.state} />
										</TableCell>
										<TableCell className="whitespace-nowrap text-right tabular-nums">
											{seller ? `${num(pt?.sellersRegistered ?? 0)} прод. · ${num(pt?.sellersApproved ?? 0)} одобр.` : `${num(pt?.orders ?? 0)} зак. · ${num(pt?.buyers ?? 0)} покуп.`}
										</TableCell>
										<TableCell className="whitespace-nowrap text-right tabular-nums">
											{rub(seller ? pt?.sellerGmv ?? 0 : pt?.revenue ?? 0)}
										</TableCell>
										<TableCell className="text-sm">{rewardLabel(p_0)}</TableCell>
										<TableCell className="whitespace-nowrap text-right tabular-nums">{rub(pt?.reward ?? 0)}</TableCell>
										<TableCell className="text-sm text-muted-foreground">{dateRu(p_0.createdAt)}</TableCell>
									</TableRow>;
            })}
						</TableBody>
					</Table>
				</div>
			</section>

			{editing && <PartnerEditorDialog open editingId={partner.id} initial={partnerInputFrom(partner)} onClose={() => setEditing(false)} onSaved={async () => {
      setEditing(false);
      await refresh();
    }} />}
			{newPromo && <PromoEditorDialog key={newPromo} open initial={blankPromoDraft(newPromo, partner.id)} onClose={() => setNewPromo(null)} onSaved={async () => {
      setNewPromo(null);
      await refresh();
      await queryClient.invalidateQueries({
        queryKey: ["promos"]
      });
    }} />}
			<ConfirmDialog open={removing} title={`Удалить партнёра «${partner.name}»?`} description="Удалить можно партнёра без промокодов. Если коды есть — выключите партнёра, история сохранится." confirmLabel="Удалить" confirmVariant="destructive" onConfirm={async () => {
      setError(null);
      try {
        await api.partners.remove(partner.id);
        await refresh();
        navigate("/promos?tab=partners");
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setRemoving(false);
      }
    }} onClose={() => setRemoving(false)} />
		</div>;
}